// #38 MilestoneTimeline (DC-30): the requisition's stations in order (Request · Approval · Posting · Intake · Panel ·
// Offer · Filled) as dots on a blue → purple progress track, the active dot glowing blue, dates under the dots. The
// station that holds the current branch is `hold`: a dashed gold-text dot and the pill *On Hold · n days*. Hold ·
// Cancel · Reopen · Archive are not stations: they are diamonds on the track at the point they happened (gold ·
// red-text · blue-text · --text-3), with the hold span shaded --t-gold. `sm` is the compact Home form; `milestones`
// is the dated closure form (Offer Accepted · Ready for HRIS · HRIS Hired · Filled · Archived). A station or event
// with href/onOpen is focusable and opens its event; `loading` draws the track as skeleton. Each station keeps at
// least 76 px (64 px sm): a narrower card scrolls the strip inside it, as a focusable region.

import type { ReactNode } from "react";
import { Icon, type IconName } from "../core/icons";
import { Pill } from "../core/Pill";
import { Skeleton } from "../core/Skeleton";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { Tooltip } from "../lib/Tooltip";
import { vars } from "../lib/vars";
import { VisuallyHidden } from "../lib/VisuallyHidden";
import { MILESTONE_LABELS, MILESTONE_TEMPLATES, PROGRESS_STATE_WORDS } from "./labels";
import { regionProps, useOverflow } from "../lib/useOverflow";

export type MilestoneState = "done" | "active" | "hold" | "next" | "skip";

export interface MilestoneStation {
  /** The station's name, from a labels module (MILESTONE_LABELS). */
  label: string;
  /** Default next. */
  state?: MilestoneState | undefined;
  /** The date under the dot (a formatted date). */
  date?: ReactNode | undefined;
  icon?: IconName | undefined;
  /** Opens the station's event. */
  href?: string | undefined;
  onOpen?: (() => void) | undefined;
  hs?: Hs | undefined;
}

export type MilestoneEventKind = "hold" | "cancel" | "reopen" | "archive";

export interface MilestoneEvent {
  kind: MilestoneEventKind;
  /** Where on the track, in station units: 0 is the first station, 2.5 halfway between the third and fourth. */
  at: number;
  date?: ReactNode | undefined;
  href?: string | undefined;
  hs?: Hs | undefined;
}

export interface MilestoneTimelineProps {
  stations: readonly MilestoneStation[];
  events?: readonly MilestoneEvent[] | undefined;
  /** The hold span on the track, in station units. */
  holdSpan?: { from: number; to: number } | undefined;
  /** Days on Hold for the `hold` station's pill. */
  holdDays?: number | undefined;
  /** The list's name (*Requisition timeline*). */
  label: string;
  sm?: boolean | undefined;
  /** The dated closure form. */
  milestones?: boolean | undefined;
  loading?: boolean | undefined;
  className?: string | undefined;
}

const EVENT_WORD: Readonly<Record<MilestoneEventKind, string>> = {
  hold: MILESTONE_LABELS.hold, cancel: MILESTONE_LABELS.cancel, reopen: MILESTONE_LABELS.reopen, archive: MILESTONE_LABELS.archive,
};

/** The state a sighted reader takes from the dot; `active` is aria-current, `hold` also shows its pill when it has days. */
const STATE_WORD: Partial<Record<MilestoneState, string>> = {
  done: PROGRESS_STATE_WORDS.completed, hold: PROGRESS_STATE_WORDS.onHold, skip: PROGRESS_STATE_WORDS.skipped,
};

/** A point on the track as a percentage of the strip: the centre of station `at`'s column. */
const point = (at: number, count: number): `${number}%` => `${Number((((at + 0.5) / count) * 100).toFixed(3))}%`;

function Station({ station, sm }: { station: MilestoneStation; sm: boolean }) {
  const hot = useHs(station.hs);
  const state = station.state ?? "next";
  const glyph = <Icon name={state === "done" ? "check" : station.icon ?? (state === "hold" ? "clock" : "target")} size={sm ? 14 : 16} />;
  const dot = station.href !== undefined
    ? <a className="mc-tl-dot" href={station.href} onClick={station.onOpen} aria-label={station.label} {...hot}>{glyph}</a>
    : station.onOpen
      ? <button type="button" className="mc-tl-dot" onClick={station.onOpen} aria-label={station.label} {...hot}>{glyph}</button>
      : <span className="mc-tl-dot" aria-hidden="true">{glyph}</span>;
  return dot;
}

function EventMark({ event, count }: { event: MilestoneEvent; count: number }) {
  const hot = useHs(event.hs);
  const word = EVENT_WORD[event.kind];
  const text = (
    <>
      <i className="mc-tl-dia" aria-hidden="true" />
      <VisuallyHidden>{word}</VisuallyHidden>
      {event.date !== undefined ? <VisuallyHidden>{event.date}</VisuallyHidden> : null}
    </>
  );
  return (
    <li className={cx("mc-tl-ev", `mc-tl-ev-${event.kind}`)} style={vars({ "--mc-at": point(event.at, count) })}>
      {event.href !== undefined
        ? <Tooltip label={word} mode="mirror"><a className="mc-tl-evl" href={event.href} {...hot}>{text}</a></Tooltip>
        : text}
    </li>
  );
}

export function MilestoneTimeline({ stations, events, holdSpan, holdDays, label, sm, milestones, loading, className }: MilestoneTimelineProps) {
  const { ref, overflows } = useOverflow<HTMLDivElement>();
  const count = Math.max(1, stations.length);
  let reached = -1;
  stations.forEach((station, index) => { if (station.state && station.state !== "next" && station.state !== "skip") reached = index; });
  const fill = reached <= 0 || count < 2 ? 0 : Number(((reached / (count - 1)) * 100).toFixed(3));
  const compact = Boolean(sm || milestones);
  if (loading) {
    return (
      <div ref={ref} className={cx("mc-tl-w", className)} {...regionProps(overflows, label)}>
        <div className={cx("mc-tl", "mc-tl-skel", compact && "mc-tl-sm")} style={vars({ "--mc-tl-n": count })} aria-busy="true">
          <span className="mc-tl-track" aria-hidden="true" />
          {stations.map((station, index) => (
            <span key={`${station.label}-${index}`} className="mc-ms" aria-hidden="true">
              <span className="mc-tl-dot mc-tl-skd" />
              <Skeleton width={56} height={12} shape="pill" className="mc-tl-skl" />
            </span>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div ref={ref} className={cx("mc-tl-w", className)} {...regionProps(overflows, label)}>
      <div
        className={cx("mc-tl", compact && "mc-tl-sm", milestones && "mc-tl-dated")}
        style={vars({ "--mc-tl-n": count, "--mc-tl-fill": `${fill}%` })}
      >
        <span className="mc-tl-track" aria-hidden="true"><b /></span>
        {holdSpan ? (
          <span
            className="mc-tl-hold"
            aria-hidden="true"
            style={vars({ "--mc-from": point(holdSpan.from, count), "--mc-to": point(holdSpan.to, count) })}
          />
        ) : null}
        <ol className="mc-tl-list" aria-label={label}>
          {stations.map((station, index) => {
            const state = station.state ?? "next";
            return (
              <li key={`${station.label}-${index}`} className={cx("mc-ms", `mc-ms-${state}`)} aria-current={state === "active" || state === "hold" ? "step" : undefined}>
                <Station station={station} sm={compact} />
                <span className="mc-ml">{station.label}</span>
                {STATE_WORD[state] !== undefined && STATE_WORD[state] !== station.label && !(state === "hold" && holdDays !== undefined)
                  ? <VisuallyHidden>{", "}{STATE_WORD[state]}</VisuallyHidden>
                  : null}
                {station.date !== undefined && station.date !== null ? <span className="mc-md">{station.date}</span> : null}
                {state === "hold" && holdDays !== undefined ? (
                  <Pill tone="warn">{MILESTONE_TEMPLATES.onHoldDays({ n: holdDays })}</Pill>
                ) : null}
              </li>
            );
          })}
        </ol>
        {events && events.length ? (
          <ul className="mc-tl-evs">
            {events.map((event, index) => <EventMark key={`${event.kind}-${index}`} event={event} count={count} />)}
          </ul>
        ) : null}
      </div>
    </div>
  );
}
